import React, { useState, useEffect } from 'react';
import { Grid, InputAdornment, TextField } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import TaxCategorySelect from './form/TaxCategorySelect';
import TaxExemptionReasonCodeSelect from './form/TaxExemptionReasonCodeSelect';
import { useSelector } from 'react-redux';
import { useTranslatedMessages } from '../hooks/useTranslatedMessages';
import { isOutOfScopeDocumentType } from '../utils/documentTypeRules';
import { isStandardRateAdditionalCategory } from '../utils/taxCategoryUtils';
import { isMarginScheme } from '../utils/helper';
import { CHARGE, RCM_TAX_CATEGORY_CODE } from '../constants/defaults';

const TaxCategoryFields = ({
  control,
  errors,
  taxCategoryName,
  taxRateName,
  setValue,
  taxCategoryReadOnly = false,
  invoiceTypeCode,
  profileExecutionId,
  isDocumentLevel = false,
  documentLevelType,
  restrictToRcm = false,
}) => {
  const taxCategoryOptions = useSelector((state) => state.codeLists.taxCategoriesList) ?? [];
  const { common, validations } = useTranslatedMessages();
  const t = common.tax_category_field ?? {};
  const taxRateLabel = t?.tax_rate_label ?? 'Tax Rate';
  const taxExemptionReasonLabel = t?.tax_exemption_reason_label ?? 'Tax Exemption Reason';
  const outOfScopeVatCategoryMessage =
    validations?.line_item?.out_of_scope_allowed_tax_categories ??
    'For out of scope documents, only E, O, and Z VAT categories are allowed';
  const marginSchemeVatCategoryMessage =
    validations?.line_item?.margin_scheme_allowed_tax_categories ??
    'Profit margin scheme allows only standard rate additional VAT (N) tax category';
  const rcmOnlyTaxCategoryMessage =
    validations?.line_item?.rcm_only_tax_category ??
    'Only AE (Reverse Charge) tax category is allowed for RCM invoices';
  const allowanceChargeMessages = validations?.allowance_charge ?? {};
  const documentLevelChargeCannotUseNMessage =
    allowanceChargeMessages.document_level_charge_cannot_use_n ??
    'Document level charge cannot use standard rate additional VAT category';
  const documentLevelAllowanceCannotUseNMessage =
    allowanceChargeMessages.document_level_allowance_cannot_use_n ??
    'Document level allowance cannot use standard rate additional VAT category';

  const [disableTaxRate, setDisableTaxRate] = useState(false);
  const selectedTaxCategory = useWatch({ control, name: taxCategoryName });
  const getOptionDisabledReason = (option) => {
    if (restrictToRcm && option?.category !== RCM_TAX_CATEGORY_CODE) {
      return rcmOnlyTaxCategoryMessage;
    }

    if (isDocumentLevel && option?.category === 'N') {
      return documentLevelType === CHARGE
        ? documentLevelChargeCannotUseNMessage
        : documentLevelAllowanceCannotUseNMessage;
    }

    if (!isDocumentLevel && isMarginScheme(profileExecutionId) && option?.category !== 'N') {
      return marginSchemeVatCategoryMessage;
    }

    if (isOutOfScopeDocumentType(invoiceTypeCode) && !['E', 'O', 'Z'].includes(option?.category)) {
      return outOfScopeVatCategoryMessage;
    }

    return '';
  };

  const isTaxCategoryE = selectedTaxCategory === 'E';

  useEffect(() => {
    if (!restrictToRcm) return;
    if (selectedTaxCategory === RCM_TAX_CATEGORY_CODE) {
      setDisableTaxRate(true);
      return;
    }
    setValue(taxCategoryName, RCM_TAX_CATEGORY_CODE, { shouldValidate: true });
    setValue(taxRateName, 0, { shouldValidate: true });
    setDisableTaxRate(true);
  }, [restrictToRcm, selectedTaxCategory, setValue, taxCategoryName, taxRateName]);

  // Clear tax exemption reason when tax category changes away from E
  useEffect(() => {
    if (!isTaxCategoryE) {
      const reasonCodeFieldName = taxCategoryName.replace('.id', '.tax_exemption_reason_code');
      const reasonFieldName = taxCategoryName.replace('.id', '.tax_exemption_reason');
      setValue(reasonCodeFieldName, null, { shouldValidate: false });
      setValue(reasonFieldName, null, { shouldValidate: false });
    }
  }, [isTaxCategoryE, taxCategoryName, setValue]);

  return (
    <>
      <Grid size={{ xs: 13, sm: 7, md: 6 }}>
        <TaxCategorySelect
          id={taxCategoryName}
          control={control}
          errors={errors}
          name={taxCategoryName}
          readOnly={taxCategoryReadOnly || restrictToRcm}
          getOptionDisabledReason={getOptionDisabledReason}
          onChange={(selected) => {
            if (!selected) {
              setValue(taxRateName, 0, { shouldValidate: true });
              setValue(taxCategoryName, null, { shouldValidate: true });
              return;
            }
            const taxObject = taxCategoryOptions.find((t) => t.category === selected?.category) ?? null;
            const taxRate = taxObject?.rate ?? null;
            const isStandardRateAdditional = isStandardRateAdditionalCategory(taxObject?.category);
            setDisableTaxRate(!isStandardRateAdditional);

            // Only set tax rate if not N category (for N, keep existing rate)
            if (!isStandardRateAdditional) {
              setValue(taxRateName, taxRate, { shouldValidate: true });
            }
            setValue(taxCategoryName, selected?.category, { shouldValidate: true });

            // Clear tax exemption reason if changing away from E
            if (selected?.category !== 'E') {
              const reasonCodeFieldName = taxCategoryName.replace('.id', '.tax_exemption_reason_code');
              const reasonFieldName = taxCategoryName.replace('.id', '.tax_exemption_reason');
              setValue(reasonCodeFieldName, null, { shouldValidate: false });
              setValue(reasonFieldName, null, { shouldValidate: false });
            }
          }}
        />
      </Grid>

      <Grid size={{ xs: 13, sm: 7, md: 6 }}>
        <Controller
          name={taxRateName}
          control={control}
          render={({ field, fieldState }) => {
            const handleChange = (e) => {
              const value = e.target.value;
              // Only allow numbers and decimals
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                const numValue = value === '' ? 0 : parseFloat(value) || 0;
                field.onChange(Number(numValue.toFixed(2)));
              }
            };

            const formatValue = (val) => {
              if (val === '' || val === null || val === undefined) return '';
              return parseFloat(val).toFixed(2);
            };

            return (
              <TextField
                {...field}
                onChange={handleChange}
                data-testid="tax-rate-input"
                type="number"
                label={taxRateLabel}
                fullWidth
                variant="outlined"
                slotProps={{
                  htmlInput: {
                    readOnly: disableTaxRate,
                    min: 0,
                    max: 100,
                    step: '0.01',
                  },
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    readOnly: disableTaxRate,
                  },
                }}
                value={formatValue(field.value)}
                InputProps={{
                  readOnly: disableTaxRate || taxCategoryReadOnly,
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            );
          }}
        />
      </Grid>

      {isTaxCategoryE && (
        <Grid size={{ xs: 13, sm: 12, md: 12 }}>
          <TaxExemptionReasonCodeSelect
            name={taxCategoryName.replace('.id', '.tax_exemption_reason_code')}
            label={taxExemptionReasonLabel}
            control={control}
            errors={errors}
            readOnly={taxCategoryReadOnly}
            setValue={setValue}
          />
        </Grid>
      )}
    </>
  );
};

export default TaxCategoryFields;
